import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Divider, Typography, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { updateEdge } from "../../api/edgeApi";
import { getTenantLocations } from "../../api/tenantApi";

// Same fields/API as TenantList.js's edit-edge dialog (name, device.model/firmware,
// asset.assetId/type, location), scoped to the edge's own tenant. device.manufacturer,
// asset.location, cloud_transfer_enabled and tier are not editable here but PUT /edges/{id}
// replaces the whole record, so we pass the edge's current values through unchanged.
export default function EdgeEditDialog({ open, edge, accessToken, onClose, onSaved }) {
  const { t } = useTranslation();
  const isSuper = useSelector((s) => s.session?.me?.global_role === "superadmin");
  const [form, setForm] = useState({ name: "", model: "", firmware: "", assetId: "", type: "", location_id: "", valonix_serial: "" });
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !edge) return;
    setForm({
      name: edge.name ?? edge.edgeId ?? "",
      model: edge.device?.model || "",
      firmware: edge.device?.firmware || "",
      assetId: edge.asset?.assetId || "",
      type: edge.asset?.type || "",
      location_id: edge.location_id || "",
      valonix_serial: edge.valonix_serial || "",
    });
    if (accessToken && edge.tenant_id) {
      setLoadingLocations(true);
      getTenantLocations(accessToken, edge.tenant_id)
        .then((data) => setLocations(data.items || data || []))
        .catch(() => setLocations([]))
        .finally(() => setLoadingLocations(false));
    }
  }, [open, edge, accessToken]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t("edges.edit.nameEmpty"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        edgeId: form.name.trim(),
        location_id: form.location_id || null,
        device: { manufacturer: edge.device?.manufacturer || "", model: form.model, firmware: form.firmware },
        asset: { assetId: form.assetId, type: form.type, location: edge.asset?.location || "" },
        cloud_transfer_enabled: Boolean(edge.cloud_transfer_enabled),
        tier: edge.tier || "pro",
        // only a superadmin assigns the label serial; omitted = unchanged on the server
        ...(isSuper ? { valonix_serial: form.valonix_serial.trim() } : {}),
      };
      const updated = await updateEdge(accessToken, edge.id, payload);
      toast.success(t("edges.edit.saved"));
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("edges.edit.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!edge) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" } }}>
      <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>{t("edges.edit.title")}</DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={t("edges.edit.nameLabel")}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          sx={{ mb: 1 }}
        />
        <div style={{ display: "flex", gap: 16 }}>
          <TextField
            fullWidth
            margin="dense"
            label={t("edges.edit.model")}
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t("edges.edit.firmware")}
            value={form.firmware}
            onChange={(e) => setForm({ ...form, firmware: e.target.value })}
          />
        </div>
        {isSuper && (
          <TextField
            fullWidth
            margin="dense"
            label={t("edges.edit.serialLabel")}
            placeholder="VAL-2610-0001"
            value={form.valonix_serial}
            onChange={(e) => setForm({ ...form, valonix_serial: e.target.value.toUpperCase() })}
            helperText={t("edges.edit.serialHelp", { serial: edge.serial_number || t("edges.edit.serialPending") })}
            sx={{ mt: 1 }}
          />
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ color: "var(--text-muted)", mb: 1 }}>
          {t("edges.edit.assetInfo")}
        </Typography>
        <TextField fullWidth margin="dense" label={t("edges.edit.assetId")} value={form.assetId} onChange={(e) => setForm({ ...form, assetId: e.target.value })} sx={{ mb: 1 }} />
        <TextField fullWidth margin="dense" label={t("edges.edit.assetType")} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} sx={{ mb: 1 }} />
        <TextField
          select
          fullWidth
          margin="dense"
          label={t("edges.edit.location")}
          value={form.location_id}
          onChange={(e) => setForm({ ...form, location_id: e.target.value })}
          disabled={loadingLocations}
          helperText={loadingLocations ? t("edges.edit.locationsLoading") : undefined}
        >
          {locations.map((loc) => (
            <MenuItem key={loc.id} value={loc.id}>
              {loc.name} {loc.address ? `- ${loc.address}` : ""}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ color: "var(--text-muted)" }}>{t("edges.cancel")}</Button>
        <Button onClick={handleSave} disabled={submitting} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>
          {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
